import { IAnalyticsConfiguration } from '../../domain/analytics/analytics-configuration'
import { IAdStickyConfiguration } from '../../domain/conf/ads'
import { IDrivingConfiguration, IPageConfiguration } from '../../domain/conf/page'
import { logger, loggerTimeLog, TimerLabels } from '../../utils/common/logger'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { PageAgeGateService } from '../age-gate/page'
import { IKlangooSensitive } from '../common/nlp/klangoo-config'
import { IKlangooNegativeKeywords } from '../common/nlp/klangoo-config'
import { PageType } from '../common/page-types'
import { IProviderConfig } from '../common/provider-config'
import { Ad, IAd } from './ad'
import { IAdRefreshConf } from './ad'
import { AdEvents, IAdSlotsAddedEvent } from './ad/events'
import { StickyAd } from './ad/sticky'
import { IAdBiddingProviderConfig, IGptAdapter } from './adapters'
import { AdURIFormatter } from './formatters/uri'
import { IAdTargetStrategyHandler } from './targeting/strategy'
import { AdTarget } from './targeting/target'

export interface IAdServiceConfig {
    // configs
    bidding?: IAdBiddingProviderConfig[]
    provider?: IProviderConfig
    refresh?: boolean | IAdRefreshConf
    verifier?: IProviderConfig
    stickyAd?: IAdStickyConfiguration

    // subscription-related (for ux)
    adLight?: boolean
    adLightEnabled?: boolean

    // page meta (for targeting)
    ancestry?: string[]
    articleId?: string
    writers?: IPageConfiguration['writers']
    network: string
    originId?: string
    wpLegacyId?: string
    page?: PageType
    property: string
    sensitive?: IKlangooSensitive
    negative?: IKlangooNegativeKeywords[]
    siteId: string
    topics?: string
    hub?: string
    spoke?: string

    analyticsConfig?: IAnalyticsConfiguration
    pageConfig?: IPageConfiguration
}

export interface IAdService {
    initialize(conf: IAdServiceConfig): void

    update(isSubscriber: boolean): Promise<void>
}

export interface IAdTargetingConfig {
    adUnit?: string
    adTargetingParams?: string
}

export class AdService extends SimpleEvents implements IAdService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            adSizes: 'data-ad', // attr holding the desktop ad sizes in the ad div
            dataScroll: 'data-scroll-component',  // attr for mobile header scroll interaction from DCS
        },
        classes: {
            amuniversalContainer: 'amuniversal__container',
            amuniversalFlyersContainer: 'amuniversal__flyers__container',
            amuniversalRightRailAdContainer: 'amuniversal__ad__right-rail-container',
            amuniversalRightRailAdNytPuzzle: 'amuniversal__ad__right-rail__nyt-puzzle',
            breaking: 'header-breaking__wrapper',
            closeStickyAd: 'close-sticky-ad',
            // classes of dfp_ad_spacer, from DCS
            dfpAdSpacer: ['ad__section-border', 'ad__section-border--category-empty'],
            fixedBottomAdContainer: 'fixed-bottom-ad-container',
            header: 'sticky--header',  // class of header, form DCS
            headerOffset: 'header--offset',  // class for header styling from DCS
            headerShrink: 'header--shrink',  // class that indicates when mobile header has shrunk
            highImpact: 'ad__high-impact',
            rightRailItem: 'story-v2-right-rail-item', // main widget, from DCS story page
            stickyAd: 'sticky-ad', // class for sticky mobile ad
            stickyNoSubscribe: 'sticky-no-subscribe',
            stickyWithSubscribe: 'sticky-with-subscribe',
            stickyWithSubscribeNoBreaking: 'sticky-with-subscribe-no-breaking',
            subscribe: 'subscribe',  // class for mobile popup subscribe button
            topAd: ['ad__section-border', 'ad__section-border--topmost'],  // classes of top ad, from DCS
            widgetRelatedStories: 'article-content__widget-group--related-story-slot', // read next widget, from DCS
            widgetShoppingEssentials: 'article-content__widget-group--category-slot', // main widget, from DCS
        },
        ids : {
            fixedBottomAddOffset: 'fixed-bottom-ad-offset',
            header: 'header',
            loc1: 'ad-1',  // id of top-of-page ad
            secondaryNav: 'secondary-nav',
        },
        vals: {
            dataScroll: 'ts-ready',  // value for data-scroll-component attr
        },
    }

    // tslint:disable-next-line: max-line-length
    // @see https://github.com/Postmedia-Digital/content.canada.com/blob/master/newspaper.com/common/js/gpt_wallpaper.min.js
    public static Wallpaper = {
        bodyClass: 'wallpaper',
        containerId: 'wallpaper-wrapper',
        readyId: 'wallpaper-spot-left',
        selector: '#wallpaper-wrapper a',
        size: [4, 4] // tslint:disable-line: no-magic-numbers
    }

    public static Native = {
        size: [5, 5] // tslint:disable-line: no-magic-numbers
    }

    public static Interscroller = {
        sizes: [[6, 6], [7, 7]] // tslint:disable-line: no-magic-numbers
    }

    protected adLight = false
    protected refreshAd: boolean | IAdRefreshConf
    protected optOutPoliticalAds = true
    protected registered = false
    protected pageAgeGated = false
    protected pageType = ''

    // Verifier platforms

    /**
     * Stores adapter, formatter, targeter refs.
     */
    public constructor(
        protected gpt: IGptAdapter,
        protected formatter: AdURIFormatter,
        protected targeting: IAdTargetStrategyHandler
    ) {
        super()
        this.synchronizeStores()
    }

    /**
     * Removes content rendered outside ad containers. Currently just CCN wallpapers.
     */
    public static destroyOOP(ads: IAd[]) {
        // CCN wallpaper
        ads
            .filter((ad) => ad.sizeRendered && ad.isWallpaper())
            .forEach(() => {
                document.body.classList.remove(AdService.Wallpaper.bodyClass)
                const el = document.getElementById(AdService.Wallpaper.containerId)
                if (el) {
                    el.remove()
                }
            })
    }

    /**
     * remove special characters from ads configure targeting.
     */
    public static removeSpecialCharacters(data: string[]) {
        return data !== undefined ? data.map((_: string) => _.replace(/[!'()*]/g, '')) : []
    }

    private static getTargetURI(targets: AdTarget | AdTarget[]): string {
        return encodeURIComponent([].concat(targets).map(
            (target: { key: string, value: string | string[] }) => {
                return target.key + '=' + target.value
            }).join('&'))

    }

    /**
     * Registers ads in DOM via primary attribute selector (slot), configure targeting.
     */
    public async initialize(conf: IAdServiceConfig) {
        logger('debug', `${TimerLabels.PAGE_LOAD} - Ads initializing`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)

        StickyAd.init(conf.stickyAd, conf.page)

        this.gpt.adURI = this.formatter(
            conf.provider.account,
            conf.siteId,
            AdService.removeSpecialCharacters(conf.ancestry),
            conf.page
        )

        if (conf.pageConfig && conf.pageConfig.ageGate && conf.pageConfig.ageGate.enabled) {
            this.pageAgeGated = true
        }

        this.gpt.registerVendors()

        this.pageType = conf.page
        const drivingTaxonomy: IDrivingConfiguration = (conf.pageConfig && conf.pageConfig.drivingTaxonomy) || {}

        // configure page level targeting (applies to all ads on a page)
        this.gpt.target(
            this.targeting.page({
                ancestry: conf.ancestry,
                articleId: conf.articleId,
                bodyType: drivingTaxonomy.bodyType,
                carMake: drivingTaxonomy.carMake,
                carModel: drivingTaxonomy.carModel,
                hub: conf.hub,
                lang: drivingTaxonomy.lang,
                modelYears: drivingTaxonomy.modelYears,
                negative: conf.negative,
                optOutPoliticalAds: this.optOutPoliticalAds,
                originId: conf.originId,
                property: conf.property,
                sensitive: conf.sensitive,
                specialSection: drivingTaxonomy.specialSection,
                spoke: conf.spoke,
                topics: conf.topics,
                type: conf.page,
                vehicleType: drivingTaxonomy.vehicleType,
                wpLegacyId: conf.wpLegacyId,
                writers: conf.writers
            }))

        this.refreshAd = conf.refresh
        this.registerNewAdsEventListener()
        // enable adlight to be disabled for a property
        const adLightEnabled = conf.adLightEnabled

        await this.update(conf.adLight && adLightEnabled)
    }

    /**
     * Returns Ad Targeting attributes as a URI for the given configuration.
     */
    public async getTargeting(conf: IAdServiceConfig): Promise<IAdTargetingConfig> {
        const adUnit = this.formatter(
            conf.provider.account,
            conf.siteId,
            AdService.removeSpecialCharacters(conf.ancestry),
            conf.page
        )

        const drivingTaxonomy: IDrivingConfiguration = (conf.pageConfig && conf.pageConfig.drivingTaxonomy) || {}
        const pageTargetingConfig = {
            ancestry: conf.ancestry,
            articleId: conf.articleId,
            bodyType: drivingTaxonomy.bodyType,
            carMake: drivingTaxonomy.carMake,
            carModel: drivingTaxonomy.carModel,
            hub: conf.hub,
            lang: drivingTaxonomy.lang,
            modelYears: drivingTaxonomy.modelYears,
            negative: conf.negative,
            optOutPoliticalAds: this.optOutPoliticalAds,
            originId: conf.originId,
            property: conf.property,
            sensitive: conf.sensitive,
            specialSection: drivingTaxonomy.specialSection,
            spoke: conf.spoke,
            topics: conf.topics,
            type: conf.page,
            vehicleType: drivingTaxonomy.vehicleType,
            writers: conf.writers
        }

        const adTargetingParams = AdService.getTargetURI(this.targeting.page(pageTargetingConfig))

        return { adUnit, adTargetingParams }
    }

    public async update(adLight: boolean) {
        logger('debug', `${TimerLabels.PAGE_LOAD} - Ads refreshed - adlight is ${adLight}`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)
        this.adLight = adLight

        // if no ad-light and ads already registered then nothing to do
        if (!this.adLight && this.registered) {
            return
        }

        // if page overlay age gate is enabled, wait to register
        // ads until user passes through age gate
        if (this.pageAgeGated) {
            await PageAgeGateService.verified
        }

        logger('info', `update->registerAds()`)
        await this.registerAds()
    }

    public registerNewAdsEventListener() {
        addEventListener(AdEvents.SLOT_ADDED, async (e: IAdSlotsAddedEvent) => {
            if (e.detail.el) {
                this.registered = false
                e.detail.el.map((el: any) => {
                    return new Ad(
                        el,
                        this.targeting.ad.bind(this.targeting),
                        this.refreshAd,
                        this.adLight,
                        this.pageType
                    )
                })
                logger('info', `registerNewAdsEventListener->registerAds()`)
                await this.registerAds()
            }
        })
    }

    /**
     * Retrieve all ads from DOM.
     */
    protected getAds(): IAd[] {
        return Ad.getAdElements()
            .map((el: Element) => {
                return new Ad(el, this.targeting.ad.bind(this.targeting), this.refreshAd, this.adLight, this.pageType)
            })
            .filter((ad) => !this.adLight || ad.isAdLightIneligible())
        // Filter for ineligible ads for adlight, e.g. native ads
    }

    /**
     * Registers ads with service adapter or displays subscriber messaging.
     */
    protected async registerAds() {
        await DOMUtils.ready

        const rightRailThreshold = 1500
        const rightRailFlyersThreshold = 1600

        logger('debug', `${TimerLabels.PAGE_LOAD} - registerAds called`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)

        if (this.adLight) {
            // remove registered ads
            if (this.registered) {
                AdService.destroyOOP(this.gpt.ads)
                // remove all ads except native
                this.gpt.ads = this.gpt.ads.filter((ad) => ad.isAdLightIneligible())
                this.registered = false
            }
        }

        // already registered
        if (this.registered) {
            return
        }
        this.registered = true

        const rightRail = document.querySelector(`.${AdService.DOM.classes.amuniversalRightRailAdContainer}`)
        if (rightRail) {
            if (
                window.innerWidth < rightRailThreshold &&
                document.querySelectorAll(`.${AdService.DOM.classes.amuniversalContainer}`).length > 0
            ) {
                rightRail.remove()
                for (const container of document.querySelectorAll(`.${AdService.DOM.classes.amuniversalContainer}`)) {
                    container.classList.remove(AdService.DOM.classes.amuniversalContainer)
                }
            } else if (
                window.innerWidth < rightRailFlyersThreshold &&
                document.querySelectorAll(`.${AdService.DOM.classes.amuniversalFlyersContainer}`).length > 0
            ) {
                rightRail.remove()
                for (const container of document.querySelectorAll(
                    `.${AdService.DOM.classes.amuniversalFlyersContainer}`
                )) {
                    container.classList.remove(AdService.DOM.classes.amuniversalFlyersContainer)
                }
            }
        }
        const rightRailNycPuzzle = document.querySelector(`.${AdService.DOM.classes.amuniversalRightRailAdNytPuzzle}`)
        if (rightRailNycPuzzle && window.innerWidth < DOMUtils.tabledCutoff) {
            rightRailNycPuzzle.remove()
        }

        const rightRailAd = document.getElementById('ad-rr1')
        if (rightRailAd && window.innerWidth < DOMUtils.tabledCutoff) {
            rightRailAd.remove()
        }

        const newAds = this.getAds().filter((ad) => this.gpt.storeAd(ad))

        await this.gpt.initRequestAds(newAds)
    }

    /**
     * Synchronizes global>local settings (currently just political ad opt out).
     */
    private synchronizeStores() {
        // get local political ad opt out
        const localOptOut = LocalStore.get(Store.OPT_OUT_POL)

        // default to true
        this.optOutPoliticalAds = localOptOut === undefined ? true : !!localOptOut

        // begin fetch of political ad opt out from global storage
        GlobalStore.get(Store.OPT_OUT_POL)
            .then((val) => {
                this.optOutPoliticalAds = !!val

                // sync to local store, even if falsy (in order to determine if set)
                if (this.optOutPoliticalAds !== localOptOut) {
                    LocalStore.set(Store.OPT_OUT_POL, this.optOutPoliticalAds)
                }
            })
            .catch(console.log)
    }
}
